import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { apiClient, unwrap } from "../../api/client";
import { ApiError } from "../../api/problem";
import { runError, scenesQuery } from "./scenes";

/** How long a button says "Started" after the API took the run. */
export const STARTED_MS = 4000;

const drop = <T>(x: Record<string, T>, id: string) => Object.fromEntries(Object.entries(x).filter(([k]) => k !== id));

/**
 * The scene buttons of a page (WEB-SCN-01 to -04): their automations, the confirmation before a run (P-231), one run
 * at a time per scene, and what each run came to. `enabled` is false on a page without a scene button.
 */
export function useScenes(orgId: string, enabled: boolean) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const list = useQuery(scenesQuery(orgId, enabled));
  const [asking, setAsking] = useState<string>();
  const [status, setStatus] = useState<Record<string, "starting" | "started">>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const inFlight = useRef(new Set<string>());
  // The timer that ends each scene's "Started", so a second run restarts it rather than being cut short by the first.
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());

  /** A button was pressed: ask first, unless that scene's run is still in flight. */
  function ask(id: string) {
    if (!inFlight.current.has(id)) setAsking(id);
  }

  async function run(id: string) {
    if (inFlight.current.has(id)) return;
    inFlight.current.add(id);
    clearTimeout(timers.current.get(id));
    setErrors((x) => drop(x, id));
    setStatus((x) => ({ ...x, [id]: "starting" }));
    try {
      await unwrap(
        apiClient("customers").POST("/v1/automations/{automation_id}/run", {
          params: { path: { automation_id: id } },
        }),
      );
      setStatus((x) => ({ ...x, [id]: "started" }));
      timers.current.set(
        id,
        setTimeout(() => setStatus((x) => (x[id] === "started" ? drop(x, id) : x)), STARTED_MS),
      );
      void qc.invalidateQueries({ queryKey: scenesQuery(orgId, true).queryKey });
    } catch (e) {
      setStatus((x) => drop(x, id));
      setErrors((x) => ({ ...x, [id]: runError(e, t) }));
    } finally {
      inFlight.current.delete(id);
    }
  }

  return {
    /** By automation id, once read; undefined while loading or when the list cannot be read. */
    list: list.data,
    /** The server has no automations yet (501): the buttons say so instead of "Scene not found". */
    unavailable: list.error instanceof ApiError && list.error.status === 501,
    asking,
    ask,
    closeAsking: () => setAsking(undefined),
    run,
    status,
    errors,
  };
}

export type Scenes = ReturnType<typeof useScenes>;
